import { cn } from '@/lib/utils';
import { lightImpact, mediumImpact } from '@/utils/capacitorUtils';
import { IonButton } from '@ionic/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GolfIcon from './GolfIcon';
import { api } from '@/utils/api';

interface FavoriteButtonProps {
  clubId: number;
  initialValue?: boolean;
  fill?: 'clear' | 'outline' | 'solid';
  color?: string;
  className?: string;
  showText?: boolean;
  themeAware?: boolean;
  onFavoriteChange?: (isFavorite: boolean) => void;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  clubId,
  initialValue = false,
  fill = 'outline',
  color = 'light',
  className = '',
  showText = false,
  themeAware = true,
  onFavoriteChange,
}) => {
  const [isFavorite, setIsFavorite] = useState<boolean>(initialValue);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState<boolean>(false);
  const { t } = useTranslation();

  const [isAnimating, setIsAnimating] = useState(false);

  // Function to toggle favorite status with animation
  const toggleFavorite = async () => {
    if (!clubId || isTogglingFavorite) return;

    setIsTogglingFavorite(true);

    // Only animate when going from unfavorited to favorited
    if (!isFavorite) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 600);
      lightImpact();
    } else {
      mediumImpact();
    }

    try {
      // Update UI immediately for better UX
      const newFavoriteState = !isFavorite;
      setIsFavorite(newFavoriteState);

      // Then make the API call
      if (isFavorite) {
        await api.delete(`/favorites-clubs/${clubId}`);
      } else {
        await api.post(`/favorites-clubs`, { clubId });
      }

      // Notify parent component if callback provided
      if (onFavoriteChange) {
        onFavoriteChange(newFavoriteState);
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      // Revert UI changes if API call fails
      setIsFavorite(isFavorite);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <IonButton
      onClick={toggleFavorite}
      fill={fill}
      shape="round"
      color={color}
      className={`${className} rounded-full relative`}
      disabled={isTogglingFavorite}
    >
      <div className="flex items-center gap-2">
        <span className="relative inline-block">
          <GolfIcon
            name={isFavorite ? 'heart-filled' : 'heart'}
            themeAware={themeAware}
            className={cn(
              'text-xl px-[6px]',
              isFavorite ? 'text-rose-500' : 'text-foreground',
              isAnimating ? 'animate-scale-heart' : ''
            )}
          />

          {/* Burst effect - only when favoriting */}
          {isAnimating && (
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <svg
                className="animate-scale-burst text-rose-500 size-8"
                viewBox="0 0 24 24"
                fill="rgba(244, 63, 94, 0.5)"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.4626 3.99415C16.7809 2.34923 14.4404 3.01211 13.0344 4.06801C12.4578 4.50096 12.1696 4.71743 12 4.71743C11.8304 4.71743 11.5422 4.50096 10.9656 4.06801C9.55962 3.01211 7.21909 2.34923 4.53744 3.99415C1.01807 6.15294 0.221721 13.2749 8.33953 19.2834C9.88572 20.4278 10.6588 21 12 21C13.3412 21 14.1143 20.4278 15.6605 19.2834C23.7783 13.2749 22.9819 6.15294 19.4626 3.99415Z"
                  stroke="none"
                />
              </svg>
            </span>
          )}
        </span>
        {showText && (
          <span className="text-sm">{t('favorites')}</span>
        )}
      </div>
    </IonButton>
  );
};

export default FavoriteButton;